import { useState, useMemo } from "react";
import { Select, message } from "antd";
import { useSelector } from "react-redux";
import SearchBar from "../../components/searchBar";
import StatusTabs from "../../components/statusTabs";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import usePermissions from "../../hooks/usePermissions";
import { getCouponColumns, CouponRecord } from "./columns";
import { useGetComplimentaryCouponsQuery, useStartComplementaryServiceMutation, useAssignComplementaryTrainerMutation, useUnassignComplementaryTrainerMutation } from "../../services/complimentaryCoupon";
import { useGetOpenTrainersQuery } from "../../services/trainer";
import "./styles.scss";

/* ─── Module-level constants ─────────────────────────────────────────── */

const SERVICE_TYPE_OPTIONS = [
  { value: 'all',               label: 'All Services'      },
  { value: 'personal_training', label: 'Personal Training' },
  { value: 'pilates',           label: 'Pilates'            },
  { value: 'therapy',           label: 'Therapy'            },
  { value: 'ems',               label: 'EMS'                },
  { value: 'mma',               label: 'MMA'                },
];

const STATUS_TABS = [
  { key: 'all',       label: 'All'       },
  { key: 'pending',   label: 'Pending'   },
  { key: 'live',      label: 'Live'      },
  { key: 'completed', label: 'Completed' },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const Coupons = () => {
  const { hasPermission } = usePermissions();
  const canStart = hasPermission('COUPONS_START');

  const [searchText,  setSearchText]  = useState("");
  const [serviceType, setServiceType] = useState("all");
  const [activeTab,   setActiveTab]   = useState("all");
  const [page,         setPage]       = useState(1);
  const [limit,        setLimit]      = useState(10);
  const [startingKey,  setStartingKey] = useState<string | null>(null);

  // branch comes from the global header (Redux), not a per-page filter
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const { data, isLoading, refetch } = useGetComplimentaryCouponsQuery({
    status:      activeTab !== 'all' ? (activeTab as 'pending' | 'live' | 'completed') : undefined,
    search:      searchText || undefined,
    serviceType: serviceType !== 'all' ? serviceType : undefined,
    branchId:    selectedBranchId || undefined,
    page,
    limit,
  });

  const [startComplementaryService] = useStartComplementaryServiceMutation();
  const [assignComplementaryTrainer] = useAssignComplementaryTrainerMutation();
  const [unassignComplementaryTrainer] = useUnassignComplementaryTrainerMutation();
  const [assigningKey, setAssigningKey] = useState<string | null>(null);

  const { data: trainersData } = useGetOpenTrainersQuery({ page: 1, limit: 100, branchId: selectedBranchId || undefined });
  const trainerOptions = useMemo(() => {
    const td = trainersData as any;
    const list = td?.data ?? td?.trainers;
    return Array.isArray(list)
      ? list.map((t: any) => ({ label: t?.user?.name ?? t?.name, value: t?._id ?? t?.id })).filter((o: any) => o.label && o.value)
      : [];
  }, [trainersData]);

  const apiData = data as any;
  const records: CouponRecord[] = apiData?.data ?? [];
  const total: number = apiData?.total ?? apiData?.pagination?.total ?? records.length;
  const counts = apiData?.counts ?? {};
  const allCount = (counts.pending ?? 0) + (counts.live ?? 0) + (counts.completed ?? 0);

  const statusTabsWithCounts = STATUS_TABS.map(tab => ({
    ...tab,
    count: tab.key === 'all' ? allCount : (counts[tab.key] ?? 0),
  }));

  const handleStart = async (record: CouponRecord) => {
    if (!record.membershipId || !record.couponId || !record.sessionId) return;
    const key = record.sessionId;
    setStartingKey(key);
    try {
      await startComplementaryService({
        membershipId: record.membershipId,
        couponId:     record.couponId,
        sessionId:    record.sessionId,
      }).unwrap();
      // message.success('Session started successfully');
      refetch();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setStartingKey(null);
    }
  };

  const handleAssignTrainer = async (record: CouponRecord, coachId: string) => {
    if (!record.membershipId || !record.serviceType) return;
    const key = `${record.membershipId}-${record.serviceType}`;
    setAssigningKey(key);
    try {
      await assignComplementaryTrainer({
        membershipId: record.membershipId,
        serviceType:  record.serviceType,
        coachId,
      }).unwrap();
      message.success('Trainer assigned successfully');
      refetch();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setAssigningKey(null);
    }
  };

  const handleUnassignTrainer = async (record: CouponRecord) => {
    if (!record.membershipId || !record.serviceType) return;
    const key = `${record.membershipId}-${record.serviceType}`;
    setAssigningKey(key);
    try {
      await unassignComplementaryTrainer({
        membershipId: record.membershipId,
        serviceType:  record.serviceType,
      }).unwrap();
      message.success('Trainer unassigned successfully');
      refetch();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setAssigningKey(null);
    }
  };

  const columns = getCouponColumns(handleStart, canStart, startingKey, handleAssignTrainer, trainerOptions, assigningKey, handleUnassignTrainer);

  return (
    <div className="coupons-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(val: string) => { setSearchText(val); setPage(1); }}
            placeholder="Search by name or phone..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={statusTabsWithCounts}
          />
        </div>
        <div className="right-col">
          <Select
            value={serviceType}
            onChange={(val: string) => { setServiceType(val); setPage(1); }}
            style={{ width: 180, height: 41 }}
            options={SERVICE_TYPE_OPTIONS}
          />
        </div>
      </div>

      <div className="coupons-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isLoading}
          pagination={false}
          rowKey={(record: CouponRecord, index: number) => record.sessionId ?? `${record.membershipId}-${record.serviceType}-${index}`}
          scroll={{ x: 1200 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={total}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default Coupons;
